import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type {
  OtpAuthType,
  OtpConfirmationControlHandle,
  OtpDetails
} from 'ehs-react';
import {
  Breakpoints,
  Button,
  intl,
  intlFmt,
  NotificationType,
  notify,
  OtpConfirmationControl,
  QRCode,
  showError,
  Tool,
  useBreakpoint,
  toast
} from 'ehs-react';
import { usePortletStore } from '../App';
import { sendTwoFactorPassword } from '../stores/ajax';
import { useLoginCredentialsStore } from '../stores/loginCredentials.store';
import { getUniqueOtpDetails } from '../util/login-response-helper';
import { PortletStoreApi } from '..';

export type OtpVerificationAreaParameters = {
  onSubmit: (
    authenticationCode: string,
    onCodeMatched: () => void,
    onCodeMismatched: (msg?: string) => void,
    isRecoveryCode?: boolean
  ) => void;
};

const useIsPatientPortal = (): boolean => {
  const url = usePortletStore(state => state.url);
  return useMemo(() => url.includes('patient-portal'), [url]);
};

export function OtpVerificationArea({
  onSubmit
}: Readonly<OtpVerificationAreaParameters>): React.JSX.Element {
  const [nrOfOtpAttempts, lengthOfCode] = usePortletStore(state => [
    state.nrOfOtpAttempts,
    state.configuration.lengthOfOtp
  ]);
  const [
    viewState,
    otpSendDetails,
    userName,
    password,
    assertionId,
    secondFactorMethod,
    qrCode,
    appSecretKey,
    otpAuthUri,
    pageLoading
  ] = useLoginCredentialsStore(state => [
    state.viewState,
    state.otpSendDetails,
    state.userName,
    state.password,
    state.assertionId,
    state.selectedTwoFactorMethod?.sendMethod,
    state.qrCode,
    state.appSecretKey,
    state.otpAuthUri,
    state.pageLoading
  ]);
  const isMobile = useBreakpoint(Breakpoints.MOBILE);
  const isPatientPortal = useIsPatientPortal();
  const sendAuthenticationCodeCallHandler = useCallback(
    (
      onSendingSuccess: (param: OtpDetails) => void,
      onSendingError: (msg?: string, resendDisabled?: boolean) => void
    ) => {
      sendTwoFactorPassword({
        url: PortletStoreApi.getState().loginServletUrl,
        user: userName,
        password: password,
        assertionId: assertionId,
        twofa: secondFactorMethod
      })
        .then(p => {
          const otpDetails = getUniqueOtpDetails(p.data);
          if (otpDetails) {
            onSendingSuccess(otpDetails);
          }
        })
        .catch(e => {
          showError(e);
          onSendingError();
        });
    },
    [assertionId, password, userName, secondFactorMethod]
  );
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);
  const [showOTPInput, setShowOTPInput] = useState(true);
  const controlRef = useRef<OtpConfirmationControlHandle>(null);
  const toastedForRef = useRef<OtpDetails | undefined>(undefined);

  const validateAuthenticationCodeCallHandler = useCallback(
    (
      authenticationCode: string,
      onCodeMatched: () => void,
      onCodeMismatched: (msg?: string) => void,
      onRequestStatusChanged: (pending: boolean) => void,
      authType?: OtpAuthType
    ) => {
      const isRecoveryCode = authType === 'RECOVERY';
      onRequestStatusChanged(true);

      const onCodeMatchedWrapped = () => {
        onRequestStatusChanged(false);
        onCodeMatched();
      };

      const onCodeMismatchedWrapped = (msg?: string) => {
        onRequestStatusChanged(false);
        onCodeMismatched(msg);
      };

      try {
        onSubmit(
          authenticationCode,
          onCodeMatchedWrapped,
          onCodeMismatchedWrapped,
          isRecoveryCode
        );
      } catch (e) {
        onRequestStatusChanged(false);
        throw e;
      }
    },
    [onSubmit]
  );

  useEffect(() => {
    if (nrOfOtpAttempts > 0) {
      controlRef.current?.reset();
    }
  }, [nrOfOtpAttempts]);

  // Reset state when switching between OTP and Recovery Code modes
  useEffect(() => {
    controlRef.current?.reset();
    setIsNextButtonDisabled(true);
  }, [showOTPInput]);

  const copyLink = useCallback(() => {
    if (appSecretKey) {
      navigator.clipboard.writeText(appSecretKey);
      notify({
        type: NotificationType.SUCCESS,
        title: intl(
          'twofactorconfigportlet.view.config.totp.secret.copy.successtoast'
        )
      });
    }
  }, [appSecretKey]);

  // Show "code sent" toast once per OtpDetails object. The ref persists across
  // OtpConfirmationControl remounts (this component stays mounted), so
  // back-navigation does not re-trigger the toast for the same OTP.
  useEffect(() => {
    if (!otpSendDetails || otpSendDetails === toastedForRef.current) return;
    toastedForRef.current = otpSendDetails;
    let toastMsg: string | undefined;
    if (otpSendDetails.sendMethod === 'EMAIL') {
      toastMsg = intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSentViaEmailToast',
        otpSendDetails.recipient
      );
    } else if (otpSendDetails.sendMethod === 'SMS') {
      toastMsg = intlFmt(
        'patientSelfOnboardingPortlet.authenticationWizardOtpConfirmationPage.activationCodeSentViaSmsToast',
        otpSendDetails.recipient
      );
    }
    if (toastMsg) {
      toast(toastMsg, NotificationType.SUCCESS, undefined);
    }
  }, [otpSendDetails]);

  if (!otpSendDetails || viewState !== 'TWO_FACTOR_ENTERING') {
    return <></>;
  }
  return (
    <div className="otp-verification-area progress-card-react">
      {isPatientPortal && !qrCode && (
        <div className="authentication-content-image-container">
          <img
            src="/o/LoginHook/images/authentication_wizard_otp.svg"
            role="none"
          />
        </div>
      )}
      <h2 className="font-22 sh-font">
        {showOTPInput
          ? intl(
              'patientSelfOnboardingPortlet.authenticationWizard.stepConfirmation'
            )
          : intl('loginhook.twofactor.view.form.recovery.link')}
      </h2>
      {qrCode && (
        <div className="totp-setup-area">
          <div className="qr-code-wrapper">
            <QRCode
              qrcodeSVG={qrCode}
              width={200}
              height={200}
              ariaLabelledby="qr-code-description"
            />
          </div>
          <div className="qr-code-description-area font-16">
            <p id="qr-code-description">
              {intl('twofactorconfigportlet.view.setup.app.intro')}
            </p>
            <div className="totp-secret-container">
              <b>{appSecretKey}</b>
              <Tool
                icon="copy-filled"
                tooltip={intl(
                  'twofactorconfigportlet.view.config.totp.secret.copy.tooltip'
                )}
                text=""
                className="copy-icon"
                onClick={copyLink}
                data-testid="copy-secret-action"
              />
            </div>
          </div>
          {isMobile && otpAuthUri && (
            <button
              className="launch-auth-app font-16"
              onClick={() => (window.location.href = otpAuthUri)}
            >
              {intl('twofactorconfigportlet.view.setup.app.launcher')}
            </button>
          )}
        </div>
      )}
      <OtpConfirmationControl
        key={showOTPInput ? 'otp' : 'recovery'}
        ref={controlRef}
        autoFocusFirstInput={true}
        onOtpValid={() => {}}
        onOtpEntered={code => setIsNextButtonDisabled(code.length === 0)}
        lengthOfCode={lengthOfCode}
        validateAuthenticationCodeCall={validateAuthenticationCodeCallHandler}
        sendAuthenticationCodeCall={sendAuthenticationCodeCallHandler}
        initialOtpDetails={otpSendDetails}
        disabled={pageLoading}
        autoSubmit={false}
        authType={showOTPInput ? 'OTP' : 'RECOVERY'}
      />
      <OtpContinueButton
        onContinueClick={() => {
          if (!controlRef.current) {
            return;
          }
          if (showOTPInput) {
            controlRef.current.submitOtp();
          } else {
            controlRef.current.submitRecoveryCode();
          }
        }}
        disabled={isNextButtonDisabled}
      />
      <ModeToggleLink
        showOTPInput={showOTPInput}
        onToggle={() => setShowOTPInput(prev => !prev)}
      />
    </div>
  );
}

function OtpContinueButton({
  onContinueClick,
  disabled = true
}: Readonly<{
  onContinueClick: () => void;
  disabled?: boolean;
}>): React.JSX.Element {
  const isPatientPortal = useIsPatientPortal();
  return (
    <div className="continue-button-bar">
      <Button
        scale={isPatientPortal ? 'medium' : 'default'}
        onClick={onContinueClick}
        text={intl('patientSelfOnboardingPortlet.welcomeScreen.buttonStartNow')}
        variant="primary"
        disabled={disabled}
        data-testid="otp-continue-button"
      />
    </div>
  );
}

function ModeToggleLink({
  showOTPInput,
  onToggle
}: Readonly<{
  showOTPInput: boolean;
  onToggle: () => void;
}>): React.JSX.Element {
  const linkKey = showOTPInput
    ? 'loginhook.twofactor.view.form.recovery.link'
    : 'loginhook.twofactor.view.form.otp.link';

  return (
    <div className="recovery-link-container">
      <p className="otp-resend-link-container font-16">
        <button
          className="otp-resend-link"
          onClick={onToggle}
          onKeyUp={e => {
            if (e.key === ' ') {
              onToggle();
            }
          }}
        >
          {intl(linkKey)}
        </button>
      </p>
    </div>
  );
}
